import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronDown, X, Search, Check, Loader } from 'react-feather';
import { get } from '../../services/ApiService';
import './Select.css';

export interface SelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

export interface SelectProps {
    /** Opções fixas (para lista estática) */
    options?: SelectOption[];

    /** Configuração para busca dinâmica no backend */
    remote?: {
        /** Rota do grid (ex: "vendedor") */
        router: string;
        /** Campo usado como value (ex: "id") */
        valueField: string;
        /** Campo usado como label (ex: "nome") */
        labelField: string;
        /** Filtros extras a serem enviados */
        extraFilters?: Record<string, any>;
        /** Mínimo de caracteres para iniciar busca (default: 0) */
        minChars?: number;
    };

    /** Valor atual (único ou array para múltiplo) */
    value?: string | number | (string | number)[] | null;

    /** Callback quando valor muda */
    onChange?: (value: string | number | (string | number)[] | null, options?: SelectOption | SelectOption[] | null) => void;

    /** Permite seleção múltipla */
    multiple?: boolean;

    /** Permite limpar seleção */
    clearable?: boolean;

    /** Permite busca/filtro */
    searchable?: boolean;

    /** Placeholder do input */
    placeholder?: string;

    /** Placeholder da busca */
    searchPlaceholder?: string;

    /** Texto quando não há opções */
    noOptionsText?: string;

    /** Texto enquanto carrega */
    loadingText?: string;

    /** Desabilitado */
    disabled?: boolean;

    /** Carrega opções ao montar (para remote) */
    loadOnMount?: boolean;

    /** Classe CSS adicional */
    className?: string;

    /** Tamanho: sm, md, lg */
    size?: 'sm' | 'md' | 'lg';

    /** Auto-aplicar ao selecionar (útil para filtros) */
    autoApply?: boolean;

    /** Callback ao aplicar (quando autoApply=false) */
    onApply?: (value: string | number | (string | number)[] | null) => void;

    /** Máximo de itens visíveis na lista antes de scroll */
    maxVisibleItems?: number;

    /** Mostrar contagem de selecionados */
    showSelectedCount?: boolean;
}

export default function Select({
    options: staticOptions = [],
    remote,
    value,
    onChange,
    multiple = false,
    clearable = true,
    searchable = true,
    placeholder = 'Selecione...',
    searchPlaceholder = 'Buscar...',
    noOptionsText = 'Nenhuma opção encontrada',
    loadingText = 'Carregando...',
    disabled = false,
    loadOnMount = true,
    className = '',
    size = 'md',
    autoApply = true,
    onApply,
    maxVisibleItems = 8,
    showSelectedCount = true,
}: SelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [remoteOptions, setRemoteOptions] = useState<SelectOption[]>([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const fetchIdRef = useRef(0);

    // Combina opções estáticas com remotas
    const allOptions = useMemo(() => {
        if (remote) {
            return remoteOptions;
        }
        return staticOptions;
    }, [staticOptions, remote, remoteOptions]);

    // Filtra opções baseado na busca (para opções estáticas)
    const filteredOptions = useMemo(() => {
        if (!search || remote) {
            return allOptions;
        }
        const searchLower = search.toLowerCase();
        return allOptions.filter(opt =>
            opt.label.toLowerCase().includes(searchLower)
        );
    }, [allOptions, search, remote]);

    // Converte value para array normalizado
    const selectedValues = useMemo((): (string | number)[] => {
        if (value === null || value === undefined) return [];
        if (Array.isArray(value)) return value;
        return [value];
    }, [value]);

    // Opções selecionadas como objetos
    const selectedOptions = useMemo(() => {
        return allOptions.filter(opt => selectedValues.includes(opt.value));
    }, [allOptions, selectedValues]);

    // Busca remota
    const fetchRemoteOptions = useCallback(async (searchTerm: string = '') => {
        if (!remote) return;

        const minChars = remote.minChars ?? 0;
        if (searchTerm.length < minChars && searchTerm.length > 0) return;

        const fetchId = ++fetchIdRef.current;
        setLoading(true);

        try {
            const filters = [];

            if (searchTerm) {
                filters.push({
                    name: remote.labelField,
                    operator: 'contains',
                    value: searchTerm,
                    type: 'string'
                });
            }

            if (remote.extraFilters) {
                Object.entries(remote.extraFilters).forEach(([name, config]) => {
                    filters.push({ name, ...config });
                });
            }

            const url = `/?route=${remote.router}/grid&action=list&skip=0&limit=50&filterValue=${JSON.stringify(filters)}`;
            const response = await get(url);

            if (fetchId !== fetchIdRef.current) return;

            if (response.data?.rows) {
                const options: SelectOption[] = response.data.rows.map((row: any) => ({
                    value: row[remote.valueField],
                    label: row[remote.labelField],
                }));
                setRemoteOptions(options);
            }
        } catch (error) {
            console.error('Erro ao buscar opções:', error);
        } finally {
            if (fetchId === fetchIdRef.current) {
                setLoading(false);
            }
        }
    }, [remote]);

    // Carrega opções remotas ao montar
    useEffect(() => {
        if (remote && loadOnMount) {
            fetchRemoteOptions();
        }
    }, [remote, loadOnMount, fetchRemoteOptions]);

    // Debounce da busca remota
    useEffect(() => {
        if (!remote || !isOpen) return;

        const timer = setTimeout(() => {
            fetchRemoteOptions(search);
        }, 300);

        return () => clearTimeout(timer);
    }, [search, remote, isOpen, fetchRemoteOptions]);

    // Calcula posição do dropdown (usa fixed para evitar overflow hidden)
    useEffect(() => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const dropdownHeight = maxVisibleItems * 32 + 50; // Altura estimada
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

            setDropdownStyle({
                position: 'fixed',
                top: openUpward ? 'auto' : rect.bottom + 4,
                bottom: openUpward ? window.innerHeight - rect.top + 4 : 'auto',
                left: rect.left,
                minWidth: rect.width,
            });
        }
    }, [isOpen, maxVisibleItems]);

    // Fecha dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setSearch('');
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Foca no input de busca ao abrir
    useEffect(() => {
        if (isOpen && searchable && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen, searchable]);

    // Scroll para item destacado
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const items = listRef.current.querySelectorAll('.select-option');
            const item = items[highlightedIndex] as HTMLElement;
            if (item) {
                item.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    const handleToggle = () => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            setSearch('');
            setHighlightedIndex(-1);
        }
    };

    const handleSelect = (option: SelectOption) => {
        if (option.disabled) return;

        let newValue: string | number | (string | number)[] | null;
        let newOptions: SelectOption | SelectOption[] | null;

        if (multiple) {
            const isSelected = selectedValues.includes(option.value);
            if (isSelected) {
                newValue = selectedValues.filter(v => v !== option.value);
            } else {
                newValue = [...selectedValues, option.value];
            }
            newOptions = allOptions.filter(opt =>
                (newValue as (string | number)[]).includes(opt.value)
            );
        } else {
            newValue = option.value;
            newOptions = option;
            setIsOpen(false);
            setSearch('');
        }

        onChange?.(newValue, newOptions);

        if (autoApply) {
            onApply?.(newValue);
        }
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newValue = multiple ? [] : null;
        onChange?.(newValue, multiple ? [] : null);
        if (autoApply) {
            onApply?.(newValue);
        }
    };

    const handleRemoveTag = (e: React.MouseEvent, optionValue: string | number) => {
        e.stopPropagation();
        if (multiple) {
            const newValue = selectedValues.filter(v => v !== optionValue);
            const newOptions = allOptions.filter(opt => newValue.includes(opt.value));
            onChange?.(newValue, newOptions);
            if (autoApply) {
                onApply?.(newValue);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setSearch('');
                setHighlightedIndex(-1);
                break;
        }
    };

    // Render do valor selecionado
    const renderValue = () => {
        if (selectedOptions.length === 0) {
            return <span className="select-placeholder">{placeholder}</span>;
        }

        if (multiple) {
            if (selectedOptions.length === 1) {
                // 1 item: mostra só o label (X geral já serve para limpar)
                return <span className="select-single-value">{selectedOptions[0].label}</span>;
            }
            // 2+ itens: mostra contagem
            return (
                <span className="select-count">
                    {selectedOptions.length} selecionados
                </span>
            );
        }

        return <span className="select-single-value">{selectedOptions[0]?.label}</span>;
    };

    const hasValue = selectedValues.length > 0;

    return (
        <div
            ref={containerRef}
            className={`select-container select-${size} ${isOpen ? 'select-open' : ''} ${disabled ? 'select-disabled' : ''} ${className}`}
            onKeyDown={handleKeyDown}
            tabIndex={disabled ? -1 : 0}
        >
            {/* Trigger */}
            <div ref={triggerRef} className="select-trigger" onClick={handleToggle}>
                <div className="select-value">
                    {renderValue()}
                </div>
                <div className="select-indicators">
                    {clearable && hasValue && !disabled && (
                        <button
                            type="button"
                            className="select-clear"
                            onClick={handleClear}
                        >
                            <X size={14} />
                        </button>
                    )}
                    <span className="select-arrow">
                        <ChevronDown size={16} />
                    </span>
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="select-dropdown" style={dropdownStyle}>
                    {/* Search */}
                    {searchable && (
                        <div className="select-search">
                            <Search size={14} className="select-search-icon" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="select-search-input"
                                placeholder={searchPlaceholder}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}

                    {/* Options List */}
                    <div
                        ref={listRef}
                        className="select-options"
                        style={{ maxHeight: maxVisibleItems * 32 }}
                    >
                        {loading ? (
                            <div className="select-loading">
                                <Loader size={16} className="select-spinner" />
                                {loadingText}
                            </div>
                        ) : filteredOptions.length === 0 ? (
                            <div className="select-no-options">{noOptionsText}</div>
                        ) : (
                            filteredOptions.map((option, index) => {
                                const isSelected = selectedValues.includes(option.value);
                                const isHighlighted = index === highlightedIndex;

                                return (
                                    <div
                                        key={option.value}
                                        className={`select-option ${isSelected ? 'select-option-selected' : ''} ${isHighlighted ? 'select-option-highlighted' : ''} ${option.disabled ? 'select-option-disabled' : ''}`}
                                        onClick={() => handleSelect(option)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    >
                                        {multiple && (
                                            <span className={`select-checkbox ${isSelected ? 'select-checkbox-checked' : ''}`}>
                                                {isSelected && <Check size={12} />}
                                            </span>
                                        )}
                                        <span className="select-option-label">{option.label}</span>
                                        {!multiple && isSelected && (
                                            <Check size={14} className="select-option-check" />
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer para múltiplo sem autoApply */}
                    {multiple && !autoApply && (
                        <div className="select-footer">
                            <button
                                type="button"
                                className="select-apply-btn"
                                onClick={() => {
                                    onApply?.(selectedValues.length > 0 ? selectedValues : null);
                                    setIsOpen(false);
                                }}
                            >
                                Aplicar
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Helper para converter filterList do backend para SelectOption[]
export function filterListToOptions(filterList: Record<string, string>): SelectOption[] {
    return Object.entries(filterList)
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}
